import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, mentorshipAPI, teamAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Users } from 'lucide-react';

interface AssignedTeam {
  _id: string;
  name: string;
  hackathon: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    status: string;
  };
  leader: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  members: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    role: string;
    joinedAt: string;
  }>;
  isApproved: boolean;
  createdAt: string;
}

interface MentorDashboardData {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileCompleted: number;
  };
  profileCompletion: number;
  upcomingSessions: Array<{
    id: string;
    mentee: string;
    topic: string;
    date: string;
    time: string;
    duration: string;
  }>;
  recentEvents: Array<{
    id: string;
    title: string;
    date: string;
    registrations: number;
    status: string;
  }>;
  assignedTeams: AssignedTeam[];
  performanceMetrics: {
    averageRating: number;
    activeMentees: number;
    eventsCreated: number;
    totalSessions: number;
  };
  statistics: {
    totalEvents: number;
    approvedEvents: number;
    totalRegistrations: number;
    pendingApproval: number;
    approvalRate: number;
    totalAssignedTeams: number;
    activeTeams: number;
    teamsWithSubmissions: number;
  };
}

interface MentorshipRequest {
  _id: string;
  aspirant: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImageUrl?: string;
    primarySkills?: string[];
    secondarySkills?: string[];
  };
  message: string;
  status: 'pending' | 'accepted' | 'rejected' | 'disconnected';
  createdAt: string;
  respondedAt?: string;
  mentorNotes?: string;
}

const MentorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<MentorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Mentorship requests state
  const [mentorshipRequests, setMentorshipRequests] = useState<MentorshipRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'accepted' | 'all'>('pending');
  
  // Modal state for responding to requests
  const [selectedRequest, setSelectedRequest] = useState<MentorshipRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [responseAction, setResponseAction] = useState<'accept' | 'reject' | 'view'>('view');
  const [mentorNotes, setMentorNotes] = useState('');
  const [responding, setResponding] = useState(false);


  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard data and assigned teams separately
      const [dashboardResponse, teamsResponse] = await Promise.all([
        dashboardAPI.getMentorDashboard(),
        teamAPI.getAssignedTeams()
      ]);
      
      if (dashboardResponse.success && dashboardResponse.data) {
        // Merge the assigned teams data from the teams API
        const dashboardData = {
          ...dashboardResponse.data,
          assignedTeams: teamsResponse || []
        };
        setDashboardData(dashboardData);
      } else {
        // If API doesn't return data, use mock data with real user info
        setDashboardData({
          user: {
            _id: user?._id || '',
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            profileCompleted: 90
          },
          profileCompletion: 90,
          upcomingSessions: [
            {
              id: '1',
              mentee: 'Sreekar Sangam',
              topic: 'Career Development Workshop',
              date: '2024-01-15',
              time: '10:00 AM',
              duration: '60 min'
            },
            {
              id: '2',
              mentee: 'Alex Chen',
              topic: 'Technical Interview Prep',
              date: '2024-01-16',
              time: '2:00 PM',
              duration: '45 min'
            }
          ],
          recentEvents: [
            {
              id: '1',
              title: 'Career Development Workshop',
              date: '2024-01-15',
              registrations: 12,
              status: 'upcoming'
            },
            {
              id: '2',
              title: 'Leadership Skills Seminar',
              date: '2024-01-10',
              registrations: 8,
              status: 'completed'
            }
          ],
          assignedTeams: teamsResponse || [],
          performanceMetrics: {
            averageRating: 4.8,
            activeMentees: 8,
            eventsCreated: 5,
            totalSessions: 24
          },
          statistics: {
            totalEvents: 5,
            approvedEvents: 4,
            totalRegistrations: 20,
            pendingApproval: 1,
            approvalRate: 80,
            totalAssignedTeams: teamsResponse?.length || 0,
            activeTeams: teamsResponse?.filter((team: any) => team.isApproved)?.length || 0,
            teamsWithSubmissions: teamsResponse?.filter((team: any) => team.submission?.title)?.length || 0
          }
        });
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      
      // Try to fetch just the teams data as fallback
      let teamsData = [];
      try {
        const teamsResponse = await teamAPI.getAssignedTeams();
        teamsData = teamsResponse || [];
      } catch (teamsErr) {
        console.error('Error fetching teams data:', teamsErr);
      }
      
      // Use mock data with real user info as fallback
      setDashboardData({
        user: {
          _id: user?._id || '',
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          profileCompleted: 90
        },
        profileCompletion: 90,
        upcomingSessions: [],
        recentEvents: [],
        assignedTeams: teamsData,
        performanceMetrics: {
          averageRating: 0,
          activeMentees: 0,
          eventsCreated: 0,
          totalSessions: 0
        },
        statistics: {
          totalEvents: 0,
          approvedEvents: 0,
          totalRegistrations: 0,
          pendingApproval: 0,
          approvalRate: 0,
          totalAssignedTeams: teamsData.length,
          activeTeams: teamsData.filter((team: any) => team.isApproved).length,
          teamsWithSubmissions: teamsData.filter((team: any) => team.submission?.title).length
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('MentorDashboard useEffect - user:', user);
    console.log('MentorDashboard useEffect - user role:', user?.role);
    
    if (user?.role === 'mentor') {
      console.log('User is mentor, fetching mentorship requests...');
      fetchMentorshipRequests();
    } else {
      console.log('User is not mentor or user not loaded yet');
    }
  }, [user]); // Removed activeTab dependency to avoid refetching on tab change

  const fetchMentorshipRequests = async () => {
    try {
      setRequestsLoading(true);
      console.log('Fetching all mentorship requests for mentor:', user?.email);
      
      // Always fetch all requests to get proper counts
      const response = await mentorshipAPI.getMentorRequests('all', 1, 50);
      console.log('Mentorship requests response:', response);
      
      if (response.success && response.data?.requests) {
        setMentorshipRequests(response.data.requests);
        console.log('Set mentorship requests:', response.data.requests);
      } else {
        console.log('No requests found or invalid response');
      }
    } catch (err: any) {
      console.error('Error fetching mentorship requests:', err);
      console.error('Error response:', err.response?.data);
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleViewRequest = (request: MentorshipRequest) => {
    setSelectedRequest(request);
    setResponseAction('view');
    setMentorNotes('');
    setIsModalOpen(true);
  };

  const handleRespondToRequest = async (action: 'accept' | 'reject') => {
    if (!selectedRequest) return;

    try {
      setResponding(true);
      await mentorshipAPI.respondToRequest(selectedRequest._id, action, mentorNotes);
      
      // Refresh the requests
      await fetchMentorshipRequests();
      
      // Close modal
      setIsModalOpen(false);
      setSelectedRequest(null);
      setMentorNotes('');
    } catch (err: any) {
      console.error('Error responding to request:', err);
    } finally {
      setResponding(false);
    }
  };

  const handleDisconnect = async (requestId: string) => {
    try {
      await mentorshipAPI.disconnect(requestId);
      await fetchMentorshipRequests();
    } catch (err: any) {
      console.error('Error disconnecting mentorship:', err);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
    setMentorNotes('');
    setResponseAction('view');
  };

  if (loading) {
    return (
      <LoadingSpinner 
        text="Loading Dashboard"
        subtitle="Preparing your mentoring workspace..."
        size="lg"
      />
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <svg className="h-10 w-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Error Loading Dashboard</h3>
            <p className="text-gray-600 leading-relaxed">{error}</p>
          </div>
          <button 
            onClick={fetchDashboardData}
            className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface shadow-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary">
                  Mentor Dashboard
                </h1>
                <p className="text-gray-600 text-lg">Welcome back, {dashboardData?.user?.firstName || 'User'}!</p>
                
                {/* Navigation Tabs */}
                <div className="flex flex-wrap gap-3 pt-4">
                  <button
                    onClick={() => {
                      const menteesSection = document.getElementById('my-mentees-section');
                      if (menteesSection) {
                        menteesSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    My Mentees
                  </button>
                  <Link
                    to="/events"
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Events
                  </Link>
                  <Link
                    to="/hackathons"
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                    Hackathons
                  </Link>
                  <Link
                    to="/mentor/teams"
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    My Assigned Teams
                  </Link>
                  <Link
                    to="/sessions"
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Sessions
                  </Link>
                  <Link
                    to="/jobs"
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 8v10a2 2 0 002 2h4a2 2 0 002-2V8" />
                    </svg>
                    Jobs
                  </Link>
                  <Link
                    to="/mentor/profile"
                    className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-all duration-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Swapped Layout */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Main Content (Previously Right Column) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Sessions */}
            <div className="bg-surface rounded-2xl shadow-sm border border-green-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-green-900">Upcoming Sessions</h3>
                <Link
                  to="/sessions"
                  className="text-primary hover:text-green-700 font-medium text-sm"
                >
                  View All →
                </Link>
              </div>
              <div className="space-y-4">
                {dashboardData?.upcomingSessions?.length > 0 ? (
                  dashboardData.upcomingSessions.map((session) => (
                    <div key={session.id} className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors duration-200">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-green-900">{session.topic}</h4>
                        <span className="px-2 py-1 bg-primary-light text-green-800 rounded-full text-xs font-medium">
                          {session.duration}
                        </span>
                      </div>
                      <p className="text-sm text-primary mb-3">with {session.mentee}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-green-500">{session.date} at {session.time}</p>
                        <button className="px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-dark transition-colors duration-200">
                          Join Session
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-green-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-primary mb-4">No upcoming sessions</p>
                    <Link
                      to="/sessions"
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200"
                    >
                      Schedule Session
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Events */}
            <div className="bg-surface rounded-2xl shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-secondary">Recent Events</h3>
                <Link
                  to="/events"
                  className="text-primary hover:text-secondary font-medium text-sm"
                >
                  View All →
                </Link>
              </div>
              <div className="space-y-4">
                {dashboardData?.recentEvents?.length > 0 ? (
                  dashboardData.recentEvents.map((event) => (
                    <div key={event.id} className="p-4 border border-border rounded-lg hover:bg-secondary/10 transition-colors duration-200">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-secondary">{event.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          event.status === 'upcoming' 
                            ? 'bg-primary-light text-blue-800' 
                            : 'bg-primary-light text-green-800'
                        }`}>
                          {event.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <p className="text-sm text-primary">{event.registrations} registrations</p>
                          <p className="text-sm text-blue-500">{event.date}</p>
                        </div>
                        <Link
                          to={`/events/${event.id}`}
                          className="px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-dark transition-colors duration-200"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-blue-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-primary mb-4">No recent events</p>
                  </div>
                )}
              </div>
            </div>

            {/* My Assigned Teams */}
            <div className="bg-surface rounded-2xl shadow-sm border border-orange-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-orange-900">My Assigned Teams</h3>
                <Link
                  to="/mentor/teams"
                  className="text-primary hover:text-orange-700 font-medium text-sm"
                >
                  View All ({dashboardData?.statistics?.totalAssignedTeams || 0}) →
                </Link>
              </div>
              <div className="space-y-4">
                {dashboardData?.assignedTeams?.length > 0 ? (
                  dashboardData.assignedTeams.map((team) => (
                    <div key={team._id} className="p-4 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors duration-200">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-orange-900">{team.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          team.isApproved 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {team.isApproved ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-orange-700">
                          <span className="font-medium">Hackathon:</span> {team.hackathon.title}
                        </p>
                        <p className="text-sm text-orange-600">
                          <span className="font-medium">Leader:</span> {team.leader.firstName} {team.leader.lastName}
                        </p>
                        <p className="text-sm text-orange-600">
                          <span className="font-medium">Members:</span> {team.members.length + 1} total
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-orange-500">
                          Created: {new Date(team.createdAt).toLocaleDateString()}
                        </p>
                        <Link
                          to={`/teams/${team._id}`}
                          className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 transition-colors duration-200"
                        >
                          View Team
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-orange-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    <p className="text-orange-700 mb-4">No teams assigned yet</p>
                    <p className="text-sm text-orange-600">
                      You'll see teams here once an admin assigns you as a mentor.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* My Mentees - Mentorship Requests */}
            <div id="my-mentees-section" className="bg-surface rounded-2xl shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-secondary">My Mentees</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'pending'
                        ? 'bg-secondary text-white'
                        : 'bg-purple-100 text-secondary hover:bg-purple-200'
                    }`}
                  >
                    Pending ({mentorshipRequests.filter(r => r.status === 'pending').length})
                  </button>
                  <button
                    onClick={() => setActiveTab('accepted')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'accepted'
                        ? 'bg-secondary text-white'
                        : 'bg-purple-100 text-secondary hover:bg-purple-200'
                    }`}
                  >
                    Active ({mentorshipRequests.filter(r => r.status === 'accepted').length})
                  </button>
                </div>
              </div>
              
              <div className="space-y-4">
                {requestsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-secondary">Loading requests...</p>
                  </div>
                ) : mentorshipRequests.filter(r => activeTab === 'all' || r.status === activeTab).length > 0 ? (
                  mentorshipRequests.filter(r => activeTab === 'all' || r.status === activeTab).map((request) => (
                    <div key={request._id} className="p-4 border border-border rounded-lg hover:bg-secondary/10 transition-colors duration-200">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                            {request.aspirant?.profileImageUrl ? (
                              <img
                                src={request.aspirant.profileImageUrl}
                                alt={`${request.aspirant.firstName} ${request.aspirant.lastName}`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-sm font-bold">
                                {request.aspirant?.firstName?.[0] || 'U'}{request.aspirant?.lastName?.[0] || 'K'}
                              </span>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-secondary">
                              {request.aspirant?.firstName || 'Unknown'} {request.aspirant?.lastName || 'User'}
                            </h4>
                            <p className="text-xs text-secondary">{request.aspirant?.email || 'No email'}</p>
                          </div>
                        </div>
                        <Badge 
                          className={`${
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            request.status === 'accepted' ? 'bg-primary-light text-green-800' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-secondary'
                          }`}
                        >
                          {request.status}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                        {request.message}
                      </p>
                      
                      {request.aspirant?.primarySkills && request.aspirant.primarySkills.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {request.aspirant.primarySkills.slice(0, 3).map((skill, index) => (
                            <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              {skill}
                            </span>
                          ))}
                          {request.aspirant.primarySkills.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              +{request.aspirant.primarySkills.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-purple-500 mb-3">
                        <span>Requested on {new Date(request.createdAt).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleViewRequest(request)}
                          variant="outline"
                          size="sm"
                          className="text-secondary border-purple-300 hover:bg-secondary/10"
                        >
                          View Details
                        </Button>
                        
                        {request.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => {
                                setSelectedRequest(request);
                                setResponseAction('accept');
                                setIsModalOpen(true);
                              }}
                              size="sm"
                              className="bg-primary hover:bg-primary-dark text-white"
                            >
                              Accept
                            </Button>
                            <Button
                              onClick={() => {
                                setSelectedRequest(request);
                                setResponseAction('reject');
                                setIsModalOpen(true);
                              }}
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        
                        {request.status === 'accepted' && (
                          <Button
                            onClick={() => handleDisconnect(request._id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Disconnect
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-purple-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-secondary mb-4">
                      {activeTab === 'pending' ? 'No pending requests' : 'No active mentees'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activeTab === 'pending' 
                        ? 'Mentorship requests will appear here when aspirants want to connect with you'
                        : 'Your accepted mentorships will be shown here'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Profile & Stats (Previously Left Column) */}
          <div className="lg:col-span-1 space-y-8">
            {/* Profile Completion */}
            <div className="bg-surface border-border shadow-lg rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 px-8 py-6">
                <h3 className="text-xl font-bold text-secondary">Profile Completion</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Progress</span>
                    <span className="text-2xl font-bold text-primary">{dashboardData?.profileCompletion || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-500"
                      style={{ width: `${dashboardData?.profileCompletion || 0}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Complete your profile to increase your visibility to aspirants and opportunities.
                </p>
                <Link
                  to="/mentor/profile"
                  className="w-full bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center"
                >
                  Complete Profile
                </Link>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-surface border-border shadow-lg rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-8 py-6">
                <h3 className="text-xl font-bold text-secondary">Performance Metrics</h3>
              </div>
              <div className="p-8 space-y-6">
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Average Rating</span>
                  <span className="text-3xl font-bold text-primary">{dashboardData?.performanceMetrics?.averageRating || 0}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Active Mentees</span>
                  <span className="text-3xl font-bold text-primary">{dashboardData?.performanceMetrics?.activeMentees || 0}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Events Created</span>
                  <span className="text-3xl font-bold text-primary">{dashboardData?.performanceMetrics?.eventsCreated || 0}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Total Sessions</span>
                  <span className="text-3xl font-bold text-secondary">{dashboardData?.performanceMetrics?.totalSessions || 0}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Mentorship Request Response Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="sm:max-w-[600px] bg-surface border border-border shadow-xl rounded-lg z-50" style={{ backgroundColor: 'white' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-secondary">
              {responseAction === 'view' ? 'Mentorship Request Details' :
               responseAction === 'accept' ? 'Accept Mentorship Request' :
               'Reject Mentorship Request'}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {responseAction === 'view' ? 'Review the mentorship request details' :
               responseAction === 'accept' ? 'You\'re about to accept this mentorship request' :
               'You\'re about to reject this mentorship request'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              {/* Aspirant Info */}
              <div className="flex items-center space-x-3 p-4 bg-background rounded-lg">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  {selectedRequest.aspirant.profileImageUrl ? (
                    <img
                      src={selectedRequest.aspirant.profileImageUrl}
                      alt={`${selectedRequest.aspirant.firstName} ${selectedRequest.aspirant.lastName}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-bold">
                      {selectedRequest.aspirant.firstName[0]}{selectedRequest.aspirant.lastName[0]}
                    </span>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-secondary">
                    {selectedRequest.aspirant.firstName} {selectedRequest.aspirant.lastName}
                  </h4>
                  <p className="text-sm text-gray-600">{selectedRequest.aspirant.email}</p>
                  <p className="text-xs text-gray-500">
                    Requested on {new Date(selectedRequest.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              {/* Message */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Message from Aspirant
                </Label>
                <div className="p-3 bg-background rounded-lg">
                  <p className="text-gray-700">{selectedRequest.message}</p>
                </div>
              </div>
              
              {/* Skills */}
              {selectedRequest.aspirant.primarySkills && selectedRequest.aspirant.primarySkills.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Aspirant's Skills
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.aspirant.primarySkills.map((skill, index) => (
                      <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm">
                        {skill}
                      </span>
                    ))}
                    {selectedRequest.aspirant.secondarySkills?.map((skill, index) => (
                      <span key={`sec-${index}`} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Response Notes (only for accept/reject) */}
              {(responseAction === 'accept' || responseAction === 'reject') && (
                <div className="space-y-2">
                  <Label htmlFor="mentorNotes" className="text-sm font-medium text-gray-700">
                    {responseAction === 'accept' ? 'Welcome Message (Optional)' : 'Reason for Rejection (Optional)'}
                  </Label>
                  <Textarea
                    id="mentorNotes"
                    placeholder={
                      responseAction === 'accept' 
                        ? "Welcome! I'm excited to mentor you..."
                        : "Thank you for your interest, however..."
                    }
                    value={mentorNotes}
                    onChange={(e) => setMentorNotes(e.target.value)}
                    className="min-h-[100px] resize-none"
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 text-right">
                    {mentorNotes.length}/500 characters
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeModal}
              disabled={responding}
            >
              {responseAction === 'view' ? 'Close' : 'Cancel'}
            </Button>
            
            {responseAction === 'accept' && (
              <Button
                type="button"
                onClick={() => handleRespondToRequest('accept')}
                disabled={responding}
                className="bg-primary hover:bg-primary-dark"
              >
                {responding ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Accepting...
                  </>
                ) : (
                  'Accept Request'
                )}
              </Button>
            )}
            
            {responseAction === 'reject' && (
              <Button
                type="button"
                onClick={() => handleRespondToRequest('reject')}
                disabled={responding}
                className="bg-red-600 hover:bg-red-700"
              >
                {responding ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Rejecting...
                  </>
                ) : (
                  'Reject Request'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MentorDashboard; 